import { TCV, ACV, MRR, ARR } from 'customer-data-objects/lineItem/PropertyNames';
import { DEAL_AMOUNT_PREFERENCES, MARGIN_PROPERTIES } from 'products-ui-components/constants/DealAmountOptions';
import { getProperty } from 'customer-data-objects/model/ImmutableModel';
import toNumber from 'products-ui-components/utils/toNumber';
import { calculateTotalMargin } from 'products-ui-components/utils/calculations/margins';
import { calculateSubtotal } from './pricing';
import { getLargestBillingCycleLength } from 'products-ui-components/utils/approximateBillingCycleLength';
import { getDecimalPrecision } from 'products-ui-components/utils/currency';
import I18n from 'I18n';
import { roundDecimals } from './rounding';
import { getLineItems } from '../subject';
import { getProperty as getPojoProperty } from 'customer-data-objects/objects/ObjectAccessors';
/**
 * Returns whether or not the calculated deal amount using the last saved line
 * items matches the current deal amount value.
 *
 * @param {DealRecord} deal - The deal for which to compare if deal amount has
 *                            been manually overwritten.
 * @param {Integer} calculatedDealAmount - The deal amount preference calculated from line items
 * @return {Boolean} - Returns true when the current deal amount has a mismatch
 *                     with the calculated deal amount (eg. user manually updated)
 *                     dealAmount. False otherwise.
 */

export function hasMismatchedDealAmount(deal, calculatedDealAmount) {
  // casting to any because because the type ImmutableDealRecord doesn't work with ObjectRecord
  const dealAmount = toNumber(getProperty(deal, 'amount')) || 0;
  return dealAmount !== calculatedDealAmount;
}
function calculateLegacyTotals(lineItems) {
  const largestBillingCycleLength = getLargestBillingCycleLength(lineItems);
  return {
    total: calculateSubtotal(lineItems, largestBillingCycleLength),
    margin: calculateTotalMargin(lineItems, largestBillingCycleLength)
  };
}

/**
 * Returns the total and margin for the selected preference type
 *  *
 * @param {List} lineItems - The lineItems for which to calculate total and margin
 * @param {Integer} type - The deal amount preference which has been selected as a portal wide
 *                         setting.
 * @return {Object} - Returns the total and margin for the line items based on the
 *                    deal amount preference specified in type.
 */
export function calculateDealAmountByPreference(maybeLineItems, type, currencyCode) {
  const lineItems = getLineItems(maybeLineItems);
  switch (type) {
    case DEAL_AMOUNT_PREFERENCES[TCV]:
    case DEAL_AMOUNT_PREFERENCES[ACV]:
    case DEAL_AMOUNT_PREFERENCES[ARR]:
    case DEAL_AMOUNT_PREFERENCES[MRR]:
      {
        const currencyDecimalDigits = getDecimalPrecision(I18n.currencySymbols, currencyCode);
        const dealAmount = lineItems.reduce((acc, lineItem) => {
          const lineItemTotal = toNumber(getPojoProperty(lineItem, type));
          const lineItemMargin = toNumber(getPojoProperty(lineItem, MARGIN_PROPERTIES[type]));
          return {
            total: acc.total + lineItemTotal,
            margin: acc.margin + lineItemMargin
          };
        }, {
          total: 0,
          margin: 0
        });

        // Rounding to currency precision
        return {
          total: roundDecimals(dealAmount.total, currencyDecimalDigits),
          margin: roundDecimals(dealAmount.margin, currencyDecimalDigits)
        };
      }
    case DEAL_AMOUNT_PREFERENCES['legacy']:
      return calculateLegacyTotals(lineItems);
    default:
      /** @TODO should this return null or 0? */
      return {
        total: null,
        margin: null
      };
  }
}